/**
 * Authentication Context
 * Manages global authentication state, user info, and login/logout
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import tokenManager from "../services/api/token-manager";
import apiClient from "../services/api/client";
import { UserInfo } from "../types/api";
import GoogleOAuthService from "../services/auth/google-oauth";
import M365OAuthService from "../services/auth/m365-oauth";

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: UserInfo | null;
  login: (user: UserInfo) => void;
  logout: () => void;
  refreshUser: () => Promise<void>;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<UserInfo | null>(null);

  useEffect(() => {
    // Check authentication status on mount
    checkAuth();
  }, []);

  // --- NEW LISTENER FOR EXTENSION MESSAGES ---
  useEffect(() => {
    const handleExtensionMessage = (event: MessageEvent) => {
      // 1. Security Check: Only accept messages from the same window
      if (event.source !== window) return;
      // 2. Check if the message is specifically tagged as coming from your extension
      if (event.data && event.data.type === "FROM_EXTENSION") {
        const payload = event.data.payload;

        // 3. Handle the Logout Action
        if (payload.action === "SYNC_LOGOUT") {
          // Clear local storage manually to avoid infinite loop with tokenManager.clearTokens()
          localStorage.removeItem("jwt_token");
          localStorage.removeItem("refresh_token");
          localStorage.removeItem("auth_provider");
          localStorage.removeItem("token_expires_at");
          localStorage.removeItem("user_data");
          localStorage.removeItem("googleAccessToken");
          localStorage.removeItem("googleUserInfo");
          localStorage.removeItem("m365AccessToken");

          // Reset React State
          setUser(null);
          setIsAuthenticated(false);

          // Redirect to login page
          window.location.href = "/";
        }

        // ✅ Update the string to match your background script
        else if (payload.action === "GOOGLE_SYNC_LOGIN" && payload.data) {
          const { googleAccessToken, googleIdToken, user } = payload.data;

          if (googleIdToken && googleAccessToken && user) {
            // Hand the raw tokens over to the OAuth service to mint a web JWT
            GoogleOAuthService.syncFromExtension(
              googleIdToken,
              googleAccessToken,
              user,
            )
              .then((res) => {
                // Update React State
                setUser(res.user);
                setIsAuthenticated(true);

                // Force navigation to the dashboard so API clients pick up the new token
                window.location.href = "/chat";
              })
              .catch((err) => {
                console.error("Failed to sync login from extension:", err);
              });
          }
        } else if (payload.action === "M365_SYNC_LOGIN" && payload.data) {
          // Extract the keys provided by background.ts
          const { m365AccessToken, accessToken, user } = payload.data;

          // Note: The extension already sends the minted backend 'accessToken'
          if (m365AccessToken && accessToken && user) {
            M365OAuthService.syncFromExtension(m365AccessToken, user)
              .then((res) => {
                console.log("Successfully synced M365 login from extension!");
                setUser(res.user);
                setIsAuthenticated(true);

                // Force navigation to the dashboard so API clients pick up the new token
                window.location.href = "/chat";
              })
              .catch((err) => {
                console.error("Failed to sync M365 login from extension:", err);
              });
          }
        }
      }
    };

    // Attach the listener
    window.addEventListener("message", handleExtensionMessage);

    // Cleanup on unmount
    return () => window.removeEventListener("message", handleExtensionMessage);
  }, []);
  // ---------------------------------------------

  const checkAuth = async () => {
    try {
      const authenticated = tokenManager.isAuthenticated();
      setIsAuthenticated(authenticated);

      if (authenticated) {
        // Get user data from localStorage instead of API call
        const userData = tokenManager.getUserData();
        if (userData) {
          setUser(userData);
        } else {
          // If no user data stored, clear auth state
          setIsAuthenticated(false);
          tokenManager.clearTokens();
        }
      }
    } catch (error) {
      console.error("Auth check failed:", error);
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const login = (userData: UserInfo) => {
    setUser(userData);
    setIsAuthenticated(true);
  };

  const logout = async () => {
    try {
      // Call logout API
      await apiClient.post("/api/v1/auth/logout").catch(() => {
        // Ignore logout API errors
      });

      // --- NEW: SEND LOGOUT SYNC TO EXTENSION ---
      if (typeof chrome !== "undefined" && chrome.runtime) {
        // Replace with your actual Extension ID (or use your Vite env variable)
        const EXTENSION_ID = import.meta.env.VITE_EXTENSION_ID;

        chrome.runtime.sendMessage(
          EXTENSION_ID,
          { action: "Web_Sync_Logout" },
          (response: any) => {
            console.log("Extension sync logout ");
          },
        );
      }
      // ------------------------------------------
    } finally {
      // Clear local state (This will also ping the extension to logout via our token-manager updates)
      tokenManager.clearTokens();
      setUser(null);
      setIsAuthenticated(false);

      // Redirect to login
      window.location.href = "/";
    }
  };

  const refreshUser = async () => {
    if (isAuthenticated) {
      await checkAuth();
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        user,
        login,
        logout,
        refreshUser,
        checkAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
